import { Timer } from "lucide-react"
import { useEffect, useState } from "react"
import i18n from "@/i18n"
import { cn } from "@/lib/utils"

export function jobKindLabel(kind: string): string {
  const key = `jobs:kind.${kind}`
  if (i18n.exists(key)) return i18n.t(key as never)
  return kind.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase())
}

export function statusDetailLabel(detail: string): string {
  const fileProgress = /^file:(\d+)\/(\d+)$/.exec(detail)
  if (fileProgress) {
    return i18n.t("jobs:statusDetail.fileProgress", { current: fileProgress[1], total: fileProgress[2] })
  }
  const bulkProgress = /^bulk:(\d+)\/(\d+)$/.exec(detail)
  if (bulkProgress) {
    return i18n.t("jobs:statusDetail.bulkProgress", { current: bulkProgress[1], total: bulkProgress[2] })
  }
  const key = `jobs:statusDetail.${detail}`
  if (i18n.exists(key)) return i18n.t(key as never)
  const normalizedKey = `jobs:statusDetail.${detail.replace(/\s+/g, "_")}`
  if (i18n.exists(normalizedKey)) return i18n.t(normalizedKey as never)
  return detail
}

export function jobStatusLabel(status: string): string {
  const key = `jobs:status.${status}`
  if (i18n.exists(key)) return i18n.t(key as never)
  return status
}

export function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  return `${m}:${String(s).padStart(2, "0")}`
}

export function JobElapsedTimer({
  startedAt,
  active,
  className,
}: {
  startedAt: string
  active: boolean
  className?: string
}) {
  const [elapsed, setElapsed] = useState(() => Date.now() - new Date(startedAt).getTime())

  useEffect(() => {
    if (!active) return
    const tick = () => setElapsed(Date.now() - new Date(startedAt).getTime())
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [startedAt, active])

  if (elapsed < 0) return null

  return (
    <span className={cn("inline-flex items-center gap-1 text-[10px] text-ui-text-muted tabular-nums", className)}>
      <Timer className="h-3 w-3" />
      {formatElapsed(elapsed)}
    </span>
  )
}
